import React from "react";
import { useForm, Controller, Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateDealerUser } from "hooks/useDealers";
import { notify } from "utils/notify";
import { FormField } from "components/shared/FormField";
import { FormLabel } from "components/shared/FormLabel";
import { FormError, getFieldAriaProps, fieldErrorId } from "components/shared/FormError";
import {
  addDealerSchema,
  addDealerDefaultValues,
  dealerCountries,
  AddDealerFormData,
} from "./schemas/addDealerSchema";

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

interface AddDealerDialogProps {
  visible: boolean;
  onHide: () => void;
}

function TextInput({
  name,
  label,
  register,
  errors,
  isPending,
  type,
}: {
  name: keyof AddDealerFormData;
  label: string;
  register: UseFormRegister<AddDealerFormData>;
  errors: FieldErrors<AddDealerFormData>;
  isPending: boolean;
  type?: string;
}) {
  const message = errors[name]?.message;
  return (
    <FormField>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <InputText
        type={type}
        {...register(name)}
        {...getFieldAriaProps(name, message)}
        className={message ? "p-invalid" : ""}
        disabled={isPending}
      />
      <FormError id={fieldErrorId(name)}>{message}</FormError>
    </FormField>
  );
}

function PasswordInput({
  name,
  label,
  control,
  errors,
  isPending,
  feedback,
}: {
  name: "password" | "confirmPassword";
  label: string;
  control: Control<AddDealerFormData>;
  errors: FieldErrors<AddDealerFormData>;
  isPending: boolean;
  feedback?: boolean;
}) {
  const message = errors[name]?.message;
  return (
    <FormField>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Password
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            className={message ? "p-invalid" : ""}
            toggleMask
            feedback={feedback}
            disabled={isPending}
            {...getFieldAriaProps(name, message)}
          />
        )}
      />
      <FormError id={fieldErrorId(name)}>{message}</FormError>
    </FormField>
  );
}

const AddDealerDialog: React.FC<AddDealerDialogProps> = ({
  visible,
  onHide,
}) => {
  const queryClient = useQueryClient();
  const createDealerUser = useCreateDealerUser();
  const isPending = createDealerUser.isPending;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<AddDealerFormData>({
    resolver: yupResolver(addDealerSchema),
    defaultValues: addDealerDefaultValues,
  });

  const handleCancel = () => {
    reset(addDealerDefaultValues);
    onHide();
  };

  const onSubmit = (data: AddDealerFormData) => {
    createDealerUser.mutate(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        dealerName: data.dealerName,
        email: data.email,
        country: data.country,
        password: data.password,
        role: "DEALER",
        isInvited: true,
      },
      {
        onSuccess: () => {
          notify.success("Dealer created successfully");
          reset(addDealerDefaultValues);
          queryClient.invalidateQueries({ queryKey: ["dealers"] });
          onHide();
        },
        onError: (error: Error) => {
          if (error.message.includes("already exists")) {
            notify.error("Dealer with this email already exists");
          } else {
            notify.error(error.message || "Failed to create dealer");
          }
        },
      }
    );
  };

  const footer = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
        disabled={isPending}
      />
      <Button
        label="Create Dealer"
        icon="pi pi-check"
        onClick={handleSubmit(onSubmit)}
        loading={isPending}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="Add New Dealer"
      visible={visible}
      style={{ width: "600px" }}
      onHide={handleCancel}
      footer={footer}
      closable={!isPending}
      closeOnEscape={!isPending}
    >
      <form className="p-fluid" onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormGrid>
          <TextInput name="firstName" label="First Name" register={register} errors={errors} isPending={isPending} />
          <TextInput name="lastName" label="Last Name" register={register} errors={errors} isPending={isPending} />
        </FormGrid>
        <FormGrid>
          <TextInput name="dealerName" label="Dealer Name" register={register} errors={errors} isPending={isPending} />
          <FormField>
            <FormLabel htmlFor="country">Country</FormLabel>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  options={dealerCountries}
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Select Country"
                  className={errors.country ? "p-invalid" : ""}
                  disabled={isPending}
                  filter
                  showClear
                  {...getFieldAriaProps("country", errors.country?.message)}
                />
              )}
            />
            <FormError id={fieldErrorId("country")}>{errors.country?.message}</FormError>
          </FormField>
        </FormGrid>
        <TextInput name="email" label="Email" type="email" register={register} errors={errors} isPending={isPending} />
        <FormGrid>
          <PasswordInput name="password" label="Password" control={control} errors={errors} isPending={isPending} feedback />
          <PasswordInput name="confirmPassword" label="Confirm Password" control={control} errors={errors} isPending={isPending} />
        </FormGrid>
        <p
          style={{
            fontSize: "0.9rem",
            color: "var(--davey-text-secondary)",
            marginTop: "1rem",
          }}
        >
          <strong>Note:</strong> This user will be created with the Dealer role.
        </p>
      </form>
    </Dialog>
  );
};

export default AddDealerDialog;
